import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart3, TrendingUp, Smile, Frown, Meh } from 'lucide-react';

const SentimentTrendChart = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <Card className="glass border-gray-200">
        <CardContent className="py-12 text-center text-gray-500">
          No sentiment trend data yet. Keep reflecting to see your emotional journey!
        </CardContent>
      </Card>
    );
  }

  // Calculate overall stats
  const totalPositive = data.reduce((sum, d) => sum + d.positive, 0);
  const totalNeutral = data.reduce((sum, d) => sum + d.neutral, 0);
  const totalNegative = data.reduce((sum, d) => sum + d.negative, 0);
  const totalReflections = totalPositive + totalNeutral + totalNegative;
  
  const avgScore = data.reduce((sum, d) => sum + d.avg_score, 0) / data.length;
  
  const positivePercent = totalReflections > 0 ? Math.round((totalPositive / totalReflections) * 100) : 0;
  const neutralPercent = totalReflections > 0 ? Math.round((totalNeutral / totalReflections) * 100) : 0;
  const negativePercent = totalReflections > 0 ? Math.round((totalNegative / totalReflections) * 100) : 0;

  return (
    <Card className="glass border-gray-200 shadow-lg">
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <BarChart3 className="w-6 h-6 text-brand-accent" />
          <span>Emotional Insights (Last 30 Days)</span>
        </CardTitle>
        <CardDescription>Your emotional pattern overview</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-brand-accent-soft p-4 rounded-xl border border-brand-border">
            <div className="text-sm text-brand-accent font-medium mb-1">Total Reflections</div>
            <div className="text-3xl font-bold text-brand-ink">{totalReflections}</div>
          </div>
          
          <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-xl border border-green-200">
            <div className="flex items-center justify-between mb-1">
              <div className="text-sm text-green-700 font-medium">Positive</div>
              <Smile className="w-5 h-5 text-green-600" />
            </div>
            <div className="text-3xl font-bold text-green-900">{totalPositive}</div>
            <div className="text-sm text-green-700 mt-1">{positivePercent}% of total</div>
          </div>
          
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-4 rounded-xl border border-gray-200">
            <div className="flex items-center justify-between mb-1">
              <div className="text-sm text-gray-700 font-medium">Neutral</div>
              <Meh className="w-5 h-5 text-gray-600" />
            </div>
            <div className="text-3xl font-bold text-gray-900">{totalNeutral}</div>
            <div className="text-sm text-gray-700 mt-1">{neutralPercent}% of total</div>
          </div>
          
          <div className="bg-gradient-to-br from-red-50 to-red-100 p-4 rounded-xl border border-red-200">
            <div className="flex items-center justify-between mb-1">
              <div className="text-sm text-red-700 font-medium">Challenging</div>
              <Frown className="w-5 h-5 text-red-600" />
            </div>
            <div className="text-3xl font-bold text-red-900">{totalNegative}</div>
            <div className="text-sm text-red-700 mt-1">{negativePercent}% of total</div>
          </div>
        </div>

        {/* Average Sentiment Score */}
        <div className="bg-brand-accent-soft p-6 rounded-xl border border-brand-border">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-blue-700 font-medium mb-2">Average Sentiment Score</div>
              <div className="text-5xl font-bold text-blue-900">{avgScore.toFixed(2)}</div>
              <div className="text-sm text-blue-700 mt-2">Scale: -1 (Very Negative) to +1 (Very Positive)</div>
            </div>
            <TrendingUp className="w-16 h-16 text-blue-400 opacity-50" />
          </div>
          
          {/* Visual bar */}
          <div className="mt-4">
            <div className="h-3 bg-gradient-to-r from-red-500 via-gray-400 to-green-500 rounded-full relative">
              <div 
                className="absolute top-1/2 transform -translate-y-1/2 w-4 h-4 bg-white border-4 border-blue-600 rounded-full shadow-lg"
                style={{ left: `${((avgScore + 1) / 2) * 100}%`, marginLeft: '-8px' }}
              />
            </div>
            <div className="flex justify-between text-xs text-gray-600 mt-1">
              <span>-1.0</span>
              <span>0</span>
              <span>+1.0</span>
            </div>
          </div>
        </div>

        {/* Daily Breakdown Timeline */}
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-4">Daily Reflection Timeline</h4>
          <div className="space-y-2">
            {data.slice(0, 10).map((day, idx) => {
              const total = day.positive + day.neutral + day.negative;
              const posWidth = total > 0 ? (day.positive / total) * 100 : 0;
              const neuWidth = total > 0 ? (day.neutral / total) * 100 : 0;
              const negWidth = total > 0 ? (day.negative / total) * 100 : 0;
              
              return (
                <div key={idx} className="bg-white p-3 rounded-lg border border-gray-200">
                  <div className="flex justify-between items-center mb-2">
                    <div className="text-sm font-medium text-gray-700">
                      {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', weekday: 'short' })}
                    </div>
                    <div className="text-xs text-gray-500">
                      {total} reflection{total !== 1 ? 's' : ''} • Score: {day.avg_score.toFixed(2)}
                    </div>
                  </div>
                  <div className="flex h-6 rounded-full overflow-hidden">
                    {day.positive > 0 && (
                      <div 
                        className="bg-green-500 flex items-center justify-center text-xs text-white font-medium"
                        style={{ width: `${posWidth}%` }}
                      >
                        {day.positive > 0 && posWidth > 15 && day.positive}
                      </div>
                    )}
                    {day.neutral > 0 && (
                      <div 
                        className="bg-gray-400 flex items-center justify-center text-xs text-white font-medium"
                        style={{ width: `${neuWidth}%` }}
                      >
                        {day.neutral > 0 && neuWidth > 15 && day.neutral}
                      </div>
                    )}
                    {day.negative > 0 && (
                      <div 
                        className="bg-red-500 flex items-center justify-center text-xs text-white font-medium"
                        style={{ width: `${negWidth}%` }}
                      >
                        {day.negative > 0 && negWidth > 15 && day.negative}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {data.length > 10 && (
            <p className="text-xs text-gray-500 mt-3 text-center">
              Showing last 10 days • Total of {data.length} days with reflections
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default SentimentTrendChart;
